import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { ListSelectedItemsComponent } from "@components/list-selected-items/list-selected-items.component";
import { ListColumnDirective } from "@components/list/list-column.directive";
import { ListComponent as ListComponent_1 , ListComponent } from "@components/list/list.component";
import { PageComponent } from "@components/page/page.component";
import { Subject, firstValueFrom } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { SelectedListItem } from "~app/common/components/list-selected-items/listSelectedItems.models";

import { InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { RebusService } from "../rebus.service";
import { Message } from "./Message";

export interface IMessages {
    messages: IRebusMessage[];
}

export interface IRebusMessage {
    body: string;
    expiration: string;
    headers: string;
    id: number;
    priority: number;
    subject: string;
    visible: string;
}

@Component({
    selector: "rask-rebus-messages",
    templateUrl: "./rebus-messages.component.html",
    styleUrls: ["./rebus-messages.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        ListComponent_1,
        ListColumnDirective,
        ButtonComponent,
        ListSelectedItemsComponent
    ]
})
export class RebusMessagesComponent implements OnInit, OnDestroy {
    @ViewChild("messagesList") messagesList: ListComponent;
    queue: string;
    subQueue: string;
    tableData: Message[] = [];
    type: string;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly busyService = inject(BusyService);
    private readonly messages: IMessages = { messages: [] };
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly raskModalService = inject(RaskModalService);
    private readonly rebusService = inject(RebusService);

    async deleteSelectedMessages() {
        const items = this.getSelectedListItems().map(x => x.id as any);
        await firstValueFrom(this.busyService.addWork(this.rebusService.delete(this.type, this.queue, items, this.subQueue)));
        this.getMessages(this.queue);
    }

    getSelectedListItems() {
        const items = this.messagesList?.getSelectedRows();
        return ObjectUtils.isNullOrUndefined(items) ? [] : items.map(x => new SelectedListItem(x.id, x.typ, true));
    }

    hasSelectedItems() {
        return this.messagesList?.getSelectedRows().length > 0;
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.type = this.activatedRoute.snapshot.paramMap.get("type");
        this.queue = this.activatedRoute.snapshot.paramMap.get("queue");
        this.subQueue = this.activatedRoute.snapshot.paramMap.get("subQueue");
        this.getMessages(this.queue);
    }

    prompt(header: string, arg: any, format: "json" | "text"): void {
        const input = new PromptInput(InputMode.readOnly, {
            bodyField: {
                value: arg,
                label: header,
                type: format
            }
        });
        this.raskModalService.prompt(header, input, "xl").subscribe();
    }

    removeFromSelection(item: SelectedListItem) {
        const filter = (result: any) => result.id === item.id;
        this.messagesList?.removeItemFromSelectionList(filter);
    }

    async resendSelectedMessages() {
        const items = this.getSelectedListItems().map(x => x.id as any);
        await firstValueFrom(this.busyService.addWork(this.rebusService.reRun(this.type, this.queue, this.subQueue, items)));
        this.getMessages(this.queue);
    }

    private getMessages(adress: string) {
        this.busyService.addWork(this.rebusService.getMessages(this.type, adress, this.subQueue))
            .subscribe(response => {
                this.tableData = response.map(x => new Message(x));
            });
    }
}
